import React, { useEffect, useState } from "react";
import { ScrollView, Text, VStack, useToast } from "native-base";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { useMutation } from "@tanstack/react-query";

import {
  CREATE_MODALS,
  MANDATORY_COLUMN_NAMES,
  SD_SCHEDULING_TEMPLATE,
  SD_TYPES,
} from "global/constants";
import {
  SD_LANDING_PAGE,
  SD_SCHEDULED_DATA,
  SD_SCHEDULING,
} from "global/paths";
import { LiaMoneyBillSolid } from "react-icons/lia";
import { getStore } from "store/storeUtils";
import { showCustomToast, showErrorToast } from "utils/utilities";
import colors from "theme/colors";
import { sdSchedulingFile } from "api/apiCall";

import ContentContainer from "atoms/ContentContainer";
import Breadcrumbs from "atoms/Breadcrumbs";
import FormContainer from "atoms/FormContainer";
import RenderSteps from "atoms/RenderSteps";
import DropzoneXlsx from "atoms/Dropzone";
import FormSubmit from "atoms/FormSubmit";
import CreateModal from "molecules/CreateModal";
import AmplitudeHelper from "utils/analytics";

const SDScheduling = () => {
  const {
    modal,
    setModal,
    noOfFilesUploading,
    setNoOfFilesUploading,
    setIsFileUploading,
    resetLoginData,
  } = getStore();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const toast = useToast();

  const [selectedFile, setSelectedFile] = useState([]);
  const [errors, setErrors] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");

  const {
    isSuccess: isSdSchedulingSuccess,
    isError: isSdSchedulingError,
    error: sdSchedulingError,
    mutate: sdSchedulingFileUpload,
    data: sdSchedulingResponse,
  } = useMutation({
    mutationFn: (data) => sdSchedulingFile(data),
    cacheTime: 10000,
  });

  useEffect(() => {
    setIsLoading(false);
    setIsFileUploading(false);
    if (isSdSchedulingSuccess) {
      AmplitudeHelper.logEvent("file_uploaded", {
        source: "sd_scheduling_page",
      });
      showCustomToast({
        title: `${noOfFilesUploading} ${t(
          "toolTip.documentUploadedSuccessfully"
        )}`,
        status: "success",
        style: {
          backgroundColor: colors.success.polarGreen1,
          borderColor: colors.success.polarGreen3,
          borderWidth: 1,
        },
        toast,
      });
      setSelectedFile([]);
      navigate(SD_SCHEDULED_DATA, {
        state: { requestId: sdSchedulingResponse?.requestId },
      });
      if (noOfFilesUploading > 0) setNoOfFilesUploading(noOfFilesUploading - 1);
    }

    if (isSdSchedulingError) {
      AmplitudeHelper.logEvent("file_upload_failed", {
        source: "sd_scheduling_pop_up_confirmation",
      });
      showCustomToast({
        title: `errorCodes.${sdSchedulingError}`,
        status: "error",
        style: {
          backgroundColor: colors.error.dustRed,
          borderColor: colors.error.rose,
          borderWidth: 1,
        },
        toast,
      });
      if (noOfFilesUploading > 0) setNoOfFilesUploading(noOfFilesUploading - 1);
    }
  }, [isSdSchedulingSuccess, isSdSchedulingError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let f = selectedFile[0];
    let reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;
      const readData = XLSX.read(data, { type: "binary" });
      const columnNames = readData.Strings;

      const hasAllMandatoryColumns = MANDATORY_COLUMN_NAMES.every(
        (mandatoryColumnName) => {
          return columnNames.some((column) => column.t === mandatoryColumnName);
        }
      );

      if (hasAllMandatoryColumns) {
        setModal({
          ...modal,
          type: CREATE_MODALS.sdScheduling,
        });
      } else {
        showErrorToast({
          error: "documentIncorrect",
          toast,
          navigate,
          resetLoginData,
        });
      }
    };
    await reader.readAsBinaryString(f);
  };

  const handleSendData = () => {
    setIsLoading(true);
    setIsFileUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile[0]);
    formData.append(
      "expectedSalaryDisbursementDate",
      dayjs(selectedDate).format("YYYY-MM-DD")
    );
    const currentNoOfFilesUploading = noOfFilesUploading + 1;
    setNoOfFilesUploading(currentNoOfFilesUploading);
    showCustomToast({
      title: `${currentNoOfFilesUploading} ${t(
        "toolTip.documentIsBeingUploaded"
      )}`,
      status: "",
      style: {
        backgroundColor: colors.tooltip.blueBody,
        borderColor: colors.tooltip.blueBorder,
        borderWidth: 1,
        textColor: colors.neutral.charcoal,
        color: colors.neutral.charcoal,
      },
      toast,
    });
    sdSchedulingFileUpload(formData);
  };

  return (
    <>
      <ContentContainer
        title={t("salaryDisbursement.SDScheduleBox.title")}
        flex={[1, 1, 1, 0.85, 0.85]}
      >
        <Breadcrumbs
          currentPage="sideBar.sdScheduling"
          isFileUploading={isLoading}
          parentScreen={[
            { name: "sideBar.salaryDisbursement", link: SD_LANDING_PAGE },
          ]}
          style={{ marginTop: "10px" }}
        />
        <ScrollView pb="50px">
          <VStack space={5}>
            <FormContainer
              title="salaryDisbursement.scheduling.title"
              description="salaryDisbursement.scheduling.description"
              icon={<LiaMoneyBillSolid size={25} />}
            >
              <Text variant="xs">
                {t("employeeCreation.bulk.followTheseSteps")}
              </Text>
              <RenderSteps
                templateLink={SD_SCHEDULING_TEMPLATE}
                screenType={SD_TYPES.SD_SCHEDULING}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />

              <DropzoneXlsx
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                errors={errors}
                setErrors={setErrors}
                isLoading={isLoading}
                screenType={SD_SCHEDULING}
              />
            </FormContainer>
          </VStack>
        </ScrollView>
        <FormSubmit
          handleSubmit={handleSubmit}
          isSubmitDisabled={
            Object.keys(selectedFile).length === 0 ||
            selectedDate === "" ||
            isLoading
          }
          redirectBackTo={SD_LANDING_PAGE}
          isBulk
        />
      </ContentContainer>
      <CreateModal handleSendData={handleSendData} />
    </>
  );
};

export default SDScheduling;
